import { GraphContext } from '@contexts/graphs'
import { withDisplayName } from '@hocs/with-display-name'
import { ErrorMappingMsg } from '@ui/error-boundary'
import { GraphUI } from '@ui/graph/graph'
import {
  ContentfulGraphTemplate,
  type GraphType,
  graphTypeKeyMapper,
} from '@ui/graph/graph-types'
import { generateDatasetFromValues } from '@ui/graph/mapper'
import { Segment } from '@ui/segment/segment'
import { Chart as ChartJS, registerables } from 'chart.js'
import { graphql } from 'gatsby'
import React, { useCallback, useMemo, useState } from 'react'
import { useErrorBoundary } from 'react-error-boundary'

ChartJS.register(...registerables)

export const isArrayEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false
  const diff = a.filter((v, index) => v !== b[index])
  return !diff.length
}

const AllowDifferentLablels = [ContentfulGraphTemplate.BusyPeriods]

const ContentfulGraphs: Utils.EntryFC<Queries.ContentfulGraphsFragment> = ({
  content: { data },
}) => {
  const [selectedGroup, setSelectedGroup] = useState(0)
  const [selectedGraph, setSelectedGraph] = useState(0)
  const { showBoundary } = useErrorBoundary()

  const graphs: GraphType[] = useMemo(() => {
    const a = data.map((d) => {
      const {
        title,
        type,
        values,
        graphs: childGraphs,
        ...others
      } = JSON.parse(d.internal.content)
      const result: GraphType = {
        title,
        type,
        labels: [] as string[],
        datasets: [],
      }
      if (values) {
        const { labels, data, colors } = generateDatasetFromValues(
          values,
          graphTypeKeyMapper[type] || graphTypeKeyMapper.default
        )
        result.labels = labels
        result.datasets = [
          {
            ...others,
            label: title,
            backgroundColor: colors,
            data,
            animation: {
              duration: 0,
            },
          },
        ]
      } else {
        childGraphs.forEach(
          ({ values: vals, title: label, type: typ, ...otherProps }) => {
            const { labels, data, colors } = generateDatasetFromValues(
              vals,
              graphTypeKeyMapper[typ] || graphTypeKeyMapper.default
            )
            if (!result.labels || result.labels.length === 0) {
              result.labels = labels
            } else if (
              !isArrayEqual(result.labels, labels) &&
              AllowDifferentLablels.findIndex((t) => t === type) === -1
            ) {
              showBoundary({
                code: ErrorMappingMsg.ErrorContentfulGraphGroup,
              })
            }

            result.datasets.push({
              ...otherProps,
              label,
              backgroundColor: colors,
              data,
              animation: {
                duration: 0,
              },
            })
          }
        )
      }

      return result
    })
    return a
  }, [data])

  const hasTabs = graphs.length > 1
  const items = graphs.map((g, i) => ({
    label: g.title,
    value: i.toString(),
  }))

  const handleGraphChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedGroup(parseInt(event.target.value))
      setSelectedGraph(0)
    },
    []
  )

  return (
    <div className="flex w-full flex-col">
      {hasTabs && (
        <Segment
          className="my-[30px] w-fit max-w-full self-center laptop:my-[20px]"
          items={items}
          name="current dataset"
          selected={selectedGroup.toString()}
          onChange={handleGraphChange}
        />
      )}
      <GraphContext.Provider
        value={{
          selectedGraph,
          selectedGroup,
          setSelectedGraph,
          setSelectedGroup,
        }}
      >
        <GraphUI
          data={graphs[selectedGroup]}
          type={graphs[selectedGroup].type}
        />
      </GraphContext.Provider>
    </div>
  )
}

export const query = graphql`
  fragment ContentfulGraphs on ContentfulGraphs {
    ...ContentfulEntry
    data {
      internal {
        content
      }
    }
  }
`

export default withDisplayName(ContentfulGraphs, 'ContentfulGraphs')
